import { defineStore } from "pinia";
import {
  KEYCLOAK_URL,
  KEYCLOAK_REALM,
  KEYCLOAK_CLIENT_ID,
  KEYCLOAK_REDIRECT_URI,
  IS_BP_MANAGER,
  ALLOWED_USER_ROLES,
} from "@/consts";
import Keycloak from "keycloak-js";
import api from "@/api";
import { type EmployeeExtended, type InstitutionBasic } from "@/extendedTypes";
import { sentryException } from "@/sentry";
import setPostLoginURL from "@/helpers/auth/setPostLoginURL";
import { IFrameMessage, SessionStorageKeys } from "@/enums";
import { globali18n } from "@/i18n";
import type { LegacyUserRole, SupportedLocales } from "@/types";
import { ref } from "vue";
import type { LoggedUser, LoggedUserRole } from "@/graphql";
import sendMessageToOldManager from "@/helpers/routes/sendMessageToOldManager";
import emitter from "@/emitter";
import { User_Roles, type Institution } from "@/graphql/generated-types";

type LoginListener = () => void;

export interface IAuthState {
  isAuthenticated: boolean;
  loggingOut: boolean;
  token?: string | null;
  idToken?: string | null;
  id?: string | null;
  currentInstitutionId: string | null;
  privacyPolicyAccepted: boolean;
  user?: LoggedUser;
  logoutPromptTimeout?: ReturnType<typeof setTimeout> | null;
}

const DEFAULT_STATE: IAuthState = {
  isAuthenticated: false,
  loggingOut: false,
  token: null,
  idToken: null,
  id: null,
  currentInstitutionId: null,
  privacyPolicyAccepted: false,
};

const loginListeners = ref<Array<LoginListener>>([]);

export const useAuthStore = defineStore("auth", {
  state: (): IAuthState => ({
    ...DEFAULT_STATE,
  }),
  getters: {
    institutionId(state): string {
      return state.currentInstitutionId || "";
    },
    currentInstitution(state): InstitutionBasic | null {
      const institution = this.profile?.institutions.find(
        (institution: InstitutionBasic) =>
          institution.id == state.currentInstitutionId,
      );

      return institution || null;
    },
    institutionLocale(): string {
      return this.currentInstitution?.locale || "da";
    },
    isSuperUser(state): boolean {
      return (state.user?.get?.roles || []).some(
        (item) =>
          item.role === User_Roles.Superuser &&
          item.institution.id === this.institutionId,
      );
    },
    roles(state): LoggedUserRole[] {
      if (!state.user) {
        return [];
      }

      return (state.user.get.roles || []).filter(
        (role) => role.institution.id === state.currentInstitutionId,
      );
    },
    legacyRoles(state): LegacyUserRole[] | undefined {
      // Only needed for permissions when iframing old manager
      return (state.user?.get?.roles || []).map((role) => {
        return {
          role: {
            name: role.role,
          },
        };
      });
    },
    rolesAsStringArray(state): string[] {
      return (state.user?.get.roles || []).map((item) =>
        item.role.toString().toLowerCase(),
      );
    },
    isSupport(): boolean {
      return !!this.rolesAsStringArray.includes("support");
    },
    firstEmployeeProfile(state): EmployeeExtended | undefined {
      if (!state.user || state.user?.get.profiles?.length === 0) {
        return;
      }

      if (state.user.get.profiles && state.user.get.profiles.length > 0) {
        const first = state.user.get.profiles[0] as EmployeeExtended;
        if (!first) {
          return;
        }

        first.email = state.user?.get.email;

        return first;
      }
    },
    profile(state): EmployeeExtended | null {
      if (!state.user) {
        return null;
      }

      const response = {
        ...(this.firstEmployeeProfile as EmployeeExtended),
        institutions: state.user.institutions as InstitutionBasic[],
        isSupportUser: this.roles.some((role) => role.role === "SUPPORT"),
      };

      // We should use the user ID, not the employee id
      response.userId = this.user?.get.id;

      // Setting the correct institution
      const institutionProfile = (state.user?.get.profiles || []).find(
        (item) =>
          item.__typename === "Employee" &&
          item.institution.id === this.currentInstitutionId,
      );

      if (institutionProfile && institutionProfile.__typename === "Employee") {
        response.institution = institutionProfile.institution as Institution;
        response.id = institutionProfile.id;
        response.plannerFilter = institutionProfile.plannerFilter;
      }

      return response;
    },
    isNotInstitution(): boolean {
      if (!this.profile) {
        return true;
      }
      return !!(
        this.profile && this.profile.type.toLowerCase() !== "institution"
      );
    },
    profileType(): string | undefined {
      if (!this.profile) {
        return undefined;
      }

      return this.profile?.type?.toLowerCase();
    },
    showRedBorderForSupportUser() {
      // We don't need to show the red border for the support users, if the app
      // is iframed by the old manager.
      if (window.$features.iframe) {
        return false;
      }

      return this.isSupport;
    },
  },
  actions: {
    async setLogin(token: string, id: string, idToken: string) {
      this.loggingOut = false;
      this.token = token;
      this.id = id;
      this.idToken = idToken;
      await this.updateProfile();

      try {
        const session = sessionStorage.getItem(
          SessionStorageKeys.AuthModule,
        ) as string;

        this.currentInstitutionId =
          JSON.parse(session).currentInstitutionId ||
          this.profile?.institution.id;
      } catch (error) {
        sentryException(error);
        throw error;
      }

      this.updateToStorage();
    },
    async setLocale() {
      // Wait little bit if there is a state update
      const timeout = setTimeout(() => {
        clearTimeout(timeout);
        // Setting the current locale value
        globali18n.locale.value = this.institutionLocale as SupportedLocales;
      }, 100);
    },
    async updateProfile() {
      const user = await api.user.getSelf();

      // Let's check if the user has access to the manager
      const hasAccess = (user.get.roles || []).some((item) =>
        ALLOWED_USER_ROLES.includes(item.role),
      );

      if (hasAccess === false) {
        loginListeners.value.forEach((listener) => listener());
        return await this.logout();
      }

      this.user = user;

      // We shouldn't show a privacy policy on BP Manager
      if (IS_BP_MANAGER) {
        this.privacyPolicyAccepted = true;
      } else {
        this.privacyPolicyAccepted =
          this.user.get.settings?.policies?.managerPolicyAccepted || false;
      }

      this.isAuthenticated = true;
      this.id = this.user.get.id;

      this.updateToStorage();

      // Call the login listener.
      loginListeners.value.forEach((listener) => listener());
    },
    async setOldManagerLogin(token: string, institutionId: string) {
      this.token = token;
      // Can be null, if sessionStorage.getItem('currentInstitution') in the old manager
      // is not yet populated.
      this.currentInstitutionId = institutionId;

      await this.updateProfile();
    },
    logoutPrompt() {
      if (this.logoutPromptTimeout) {
        return;
      }

      emitter.emit("logoutPrompt");
      this.logoutPromptTimeout = setTimeout(async () => {
        this.logout();
      }, 5000);
    },
    async logout() {
      const idToken = this.idToken;
      this.isAuthenticated = false;
      this.loggingOut = true;
      this.token = null;
      this.idToken = null;
      this.id = null;

      this.currentInstitutionId = null;

      this.user = undefined;
      this.updateToStorage();

      const keycloak = new Keycloak({
        url: KEYCLOAK_URL,
        realm: KEYCLOAK_REALM,
        clientId: KEYCLOAK_CLIENT_ID,
      });

      await keycloak.init({ idToken: idToken || undefined });

      // Let's create a logout url
      let url = keycloak.createLogoutUrl();
      // We need to add id_token_hint value
      // https://github.com/keycloak/keycloak/discussions/12183
      url += "&id_token_hint=" + idToken;

      // Redirect to URL
      window.location.replace(url);
    },
    updateToStorage() {
      this.setLocale();

      // Update in case it's different, as it can be initialized as null.
      // This seems to be the case right after login.
      const currentInstitutionId = new URLSearchParams(
        window.location.search,
      ).get("institution_id");
      if (
        currentInstitutionId &&
        currentInstitutionId != this.currentInstitutionId
      ) {
        this.currentInstitutionId = currentInstitutionId;
      }

      const data = JSON.stringify(this.$state);
      sessionStorage.setItem(SessionStorageKeys.AuthModule, data);
    },
    async init() {
      const data = sessionStorage.getItem(SessionStorageKeys.AuthModule);
      if (data) {
        const state: IAuthState = {
          ...DEFAULT_STATE,
          ...JSON.parse(data),
        };
        // Let's update the current state
        this.$patch(state);

        // We might need to fetch the latest profile data because of default
        // values. To be able to fetcj, we had to set the state first. Because
        // API requests need token.
        if (state.isAuthenticated && state.token) {
          await this.updateProfile();
        }

        this.updateToStorage();
      }
    },
    async redirectLogin() {
      this.init();

      // We don't need to redirect to keycloak if login is successful
      if (this.isAuthenticated) {
        return;
      }

      // Let's set the current URL as a flash message to use it in the callback
      setPostLoginURL();

      const keycloak = new Keycloak({
        url: KEYCLOAK_URL,
        realm: KEYCLOAK_REALM,
        clientId: KEYCLOAK_CLIENT_ID,
      });

      await keycloak.init({
        redirectUri: KEYCLOAK_REDIRECT_URI,
        responseMode: "query",
        pkceMethod: "S256",
      });

      await keycloak.login({
        scope: "openid offline_access profile",
      });
    },
    switchInstitution(newInstitutionId: string) {
      if (!this.profile?.institutions.length) {
        sentryException("No institutions connected to profile");
        return;
      }

      this.$patch({
        currentInstitutionId: newInstitutionId,
      });

      // Let's set the current URL as a flash message to use it in the callback
      setPostLoginURL();

      this.updateToStorage();

      this.setLocale();

      sendMessageToOldManager(IFrameMessage.InstitutionChanged, {
        newId: newInstitutionId,
      });

      window.location.reload();
    },
    addLoginListener(listener: LoginListener): void {
      loginListeners.value.push(listener);
    },
    removeLoginListener(listener: LoginListener): void {
      loginListeners.value = loginListeners.value.filter(
        (item) => item !== listener,
      );
    },
    updatePlannerFilterId(id: string) {
      const newProfileState = this.user?.get.profiles?.map((profile) => {
        if ((profile as EmployeeExtended).id === this.profile?.id) {
          (profile as EmployeeExtended).plannerFilter = { id };
        }

        return profile;
      });

      this.$patch({
        user: {
          get: {
            profiles: newProfileState,
          },
        },
      });
    },
  },
});
